import './white_label.css';
import heroImage from '../../../../../assets/images/login_side_img.jpg';
import { ToastContainer } from 'react-toastify';
import { showToast } from '../../../../../utilities/helpers/ToastHelper';
import { useDictionary } from '../../../../../utilities/hooks/UseDictionary';
import {
	getPlanData,
	requestBusinessPlan,
	updateWhiteLabel
} from '../networking';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const DEFAULT_PRIMARY_COLOR = '#E55A5C';
const DEFAULT_SECONDARY_COLOR = '#22264C';

// --- Inline SVG icons (kept local — mirrors the Plan tab's approach) -------

const IconSparkle = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.7"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2 2M16 16l2 2M6 18l2-2M16 8l2-2" />
	</svg>
);

const IconShield = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3Z" />
	</svg>
);

const IconLock = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<rect x="4" y="11" width="16" height="10" rx="2" />
		<path d="M8 11V7a4 4 0 0 1 8 0v4" />
	</svg>
);

const IconStar = () => (
	<svg viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true">
		<path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.9 6.1 20.5l1.2-6.5L2.5 9.4l6.6-.9 2.9-6z" />
	</svg>
);

const IconClock = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<circle cx="12" cy="12" r="9" />
		<path d="M12 7v5l3 2" />
	</svg>
);

const IconArrowRight = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M5 12h14M13 5l7 7-7 7" />
	</svg>
);

const IconMonitor = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<rect x="3" y="4" width="18" height="12" rx="2" />
		<path d="M8 20h8M12 16v4" />
	</svg>
);

const IconPhone = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<rect x="7" y="3" width="10" height="18" rx="2" />
		<path d="M11 18h2" />
	</svg>
);

const IconInfo = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<circle cx="12" cy="12" r="9" />
		<path d="M12 11v5M12 8h.01" />
	</svg>
);

const IconImage = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<rect x="3" y="4" width="18" height="16" rx="2" />
		<circle cx="8.5" cy="9.5" r="1.5" />
		<path d="m21 16-5-5L5 20" />
	</svg>
);

const IconLeaf = () => (
	<svg
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.8"
		strokeLinecap="round"
		strokeLinejoin="round"
		aria-hidden="true"
	>
		<path d="M11 20A7 7 0 0 1 4 13c0-5 5-9 16-9-1 9-5 12-9 12Z" />
		<path d="M4 20c3-4 6-6 10-7" />
	</svg>
);

const isValidOrEmpty = (color) =>
	color.trim() === '' || HEX_COLOR_PATTERN.test(color.trim());

const ColorField = ({ label, hint, error, value, fallback, onChange, testId }) => {
	const invalid = !isValidOrEmpty(value);
	const pickerValue = HEX_COLOR_PATTERN.test(value.trim())
		? value.trim()
		: fallback;

	const onHexChange = (raw) => {
		let next = raw;
		if (next && next[0] !== '#') {
			next = '#' + next.replace(/#/g, '');
		}
		onChange(next);
	};

	return (
		<div className={invalid ? 'cfield is-invalid' : 'cfield'}>
			<label className="cfield__label">{label}</label>
			<div className={invalid ? 'cinput is-invalid' : 'cinput'}>
				<span
					className="cinput__swatch"
					style={{ '--sw': pickerValue }}
				>
					<input
						type="color"
						value={pickerValue}
						onChange={(e) => onChange(e.target.value.toUpperCase())}
						data-testid={`${testId}-picker`}
					/>
				</span>
				<input
					type="text"
					className="cinput__hex"
					value={value}
					placeholder={fallback}
					maxLength={7}
					spellCheck={false}
					onChange={(e) => onHexChange(e.target.value)}
					onBlur={(e) => onChange(e.target.value.toUpperCase())}
					data-testid={`${testId}-input`}
				/>
			</div>
			<div className="cfield__hint">{hint}</div>
			<div className="cfield__err">{error}</div>
		</div>
	);
};

ColorField.propTypes = {
	label: PropTypes.string,
	hint: PropTypes.string,
	error: PropTypes.string,
	value: PropTypes.string.isRequired,
	fallback: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
	testId: PropTypes.string.isRequired
};

const WhiteLabel = ({ info, goToPlan, goToCompanyProfile }) => {
	const dictionary = useDictionary();
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [isEnterprise, setIsEnterprise] = useState(false);
	const [enabled, setEnabled] = useState(false);
	const [primaryColor, setPrimaryColor] = useState('');
	const [secondaryColor, setSecondaryColor] = useState('');
	const [device, setDevice] = useState('desktop');
	const [isRequesting, setIsRequesting] = useState(false);

	useEffect(() => {
		let cancelled = false;
		getPlanData()
			.then((response) => {
				if (cancelled) {
					return;
				}
				const data = response?.data ?? {};
				setIsEnterprise(data.plan === 'enterprise');
				const whiteLabel = data.white_label ?? {};
				setEnabled(Boolean(whiteLabel.enabled));
				setPrimaryColor(whiteLabel.primary_color ?? '');
				setSecondaryColor(whiteLabel.secondary_color ?? '');
				setIsLoading(false);
			})
			.catch(() => {
				if (!cancelled) {
					setIsLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, []);

	const presets = [
		{
			name: dictionary.white_label_palette_upiria,
			primary: '#E55A5C',
			secondary: '#22264C'
		},
		{
			name: dictionary.white_label_palette_ocean,
			primary: '#1A73E8',
			secondary: '#0B2545'
		},
		{
			name: dictionary.white_label_palette_forest,
			primary: '#2E8B57',
			secondary: '#14342B'
		},
		{
			name: dictionary.white_label_palette_sunset,
			primary: '#F0682E',
			secondary: '#3A2018'
		},
		{
			name: dictionary.white_label_palette_plum,
			primary: '#7A4DD6',
			secondary: '#2A1B4A'
		},
		{
			name: dictionary.white_label_palette_slate,
			primary: '#0EA5A5',
			secondary: '#1F2937'
		}
	];

	const primaryValid = isValidOrEmpty(primaryColor);
	const secondaryValid = isValidOrEmpty(secondaryColor);

	const isVerified =
		info?.verification_status?.value === '3' ||
		info?.verification_status?.key === 'verified';

	// Non-enterprise providers get a playground: colors and preview work
	// locally so they experience the feature, but there is no Save — the
	// bar offers "Request Enterprise plan" instead. The API gate on PUT
	// is what actually enforces the plan.
	const previewActive = isEnterprise ? enabled : true;

	// Preview falls back to Upiria's palette when branding is off or a
	// field is empty/invalid — same as the website channel would.
	const effectivePrimary =
		previewActive && HEX_COLOR_PATTERN.test(primaryColor.trim())
			? primaryColor.trim()
			: DEFAULT_PRIMARY_COLOR;
	const effectiveSecondary =
		previewActive && HEX_COLOR_PATTERN.test(secondaryColor.trim())
			? secondaryColor.trim()
			: DEFAULT_SECONDARY_COLOR;

	const brandName =
		info?.brand_name || dictionary.white_label_preview_your_brand;
	const logoPath = info?.company_photo?.path || null;
	// Custom booking domains aren't supported yet — the channel runs on the
	// Upiria SPA host, so the preview shows that.
	const previewUrl = 'app.upiria.com';

	// Next four days, so the date pills always look current.
	const previewDates = [0, 1, 2, 3].map((offset) => {
		const date = new Date();
		date.setDate(date.getDate() + offset);
		return {
			day: date.toLocaleString('en-US', { weekday: 'short' }),
			num: date.getDate()
		};
	});

	const onPresetClicked = (preset) => {
		setPrimaryColor(preset.primary);
		setSecondaryColor(preset.secondary);
	};

	const isPresetActive = (preset) =>
		previewActive &&
		primaryColor.trim().toUpperCase() === preset.primary &&
		secondaryColor.trim().toUpperCase() === preset.secondary;

	const onResetClicked = () => {
		setPrimaryColor(DEFAULT_PRIMARY_COLOR);
		setSecondaryColor(DEFAULT_SECONDARY_COLOR);
	};

	const onRequestEnterpriseClicked = async () => {
		if (isRequesting || !isVerified) {
			return;
		}

		setIsRequesting(true);
		try {
			await requestBusinessPlan('enterprise');
			showToast({
				type: 'success',
				message: dictionary.enterprise_plan_request_sent
			});
		} catch (error) {
			showToast({
				type: 'error',
				message:
					error?.response?.data?.message ||
					dictionary.problem_requesting_enterprise_plan,
				className: 'toast-white-error'
			});
		} finally {
			setIsRequesting(false);
		}
	};

	const onSubmit = async (e) => {
		e.preventDefault();
		if (!isEnterprise || isSaving || !primaryValid || !secondaryValid) {
			return;
		}

		setIsSaving(true);
		try {
			const { data } = await updateWhiteLabel({
				enabled,
				primary_color: primaryColor.trim(),
				secondary_color: secondaryColor.trim()
			});
			setEnabled(Boolean(data?.enabled));
			setPrimaryColor(data?.primary_color ?? '');
			setSecondaryColor(data?.secondary_color ?? '');
			showToast({
				type: 'success',
				message: dictionary.white_label_saved
			});
		} catch (error) {
			showToast({
				type: 'error',
				message:
					error?.response?.data?.message ||
					dictionary.problem_saving_white_label,
				className: 'toast-white-error'
			});
		} finally {
			setIsSaving(false);
		}
	};

	if (isLoading) {
		return <div className="wl-tab thin-scrollbar fade-on-load" />;
	}

	return (
		<div className="wl-tab thin-scrollbar fade-on-load" data-testid="white-label-tab-content">
			<div className="wl">
				{/* ---- Left: form ---- */}
				<form className="wl__form" onSubmit={onSubmit}>
					<div className="wl-eyebrow">
						<IconSparkle />
						{dictionary.white_label_eyebrow}
					</div>
					<div className="wl-head">
						<h2>{dictionary.white_label_title}</h2>
						<p>{dictionary.white_label_subtitle}</p>
					</div>

					<div className="wl-divider" />

					<div className="wl-enable">
						<div className="wl-enable__txt">
							<div className="wl-enable__title">
								{dictionary.white_label_enabled_label}
								{!isEnterprise ? (
									<span className="wl-enable__chip">
										<IconLock />
										{dictionary.white_label_enterprise_chip}
									</span>
								) : null}
							</div>
							<div className="wl-enable__sub">
								{isEnterprise
									? dictionary.white_label_enabled_sub
									: dictionary.white_label_upsell_switch_sub}
							</div>
						</div>
						<label
							className={
								isEnterprise ? 'wl-switch' : 'wl-switch is-locked'
							}
						>
							<input
								type="checkbox"
								checked={isEnterprise && enabled}
								disabled={!isEnterprise}
								onChange={(e) => setEnabled(e.target.checked)}
								data-testid="white-label-enabled"
							/>
							<span className="wl-switch__track" />
							<span className="wl-switch__thumb" />
						</label>
					</div>

					<div className={previewActive ? 'wl-body' : 'wl-body is-off'}>
						<div className="wl-grouplabel">
							{dictionary.white_label_colors_group}
						</div>
						<div className="wl-groupsub">
							{dictionary.white_label_colors_group_sub}
						</div>

						<div className="wl-colors">
							<ColorField
								label={dictionary.white_label_color_label}
								hint={dictionary.white_label_color_hint}
								error={dictionary.white_label_invalid_color}
								value={primaryColor}
								fallback={DEFAULT_PRIMARY_COLOR}
								onChange={setPrimaryColor}
								testId="white-label-color"
							/>
							<ColorField
								label={dictionary.white_label_secondary_color_label}
								hint={dictionary.white_label_secondary_color_hint}
								error={dictionary.white_label_invalid_color}
								value={secondaryColor}
								fallback={DEFAULT_SECONDARY_COLOR}
								onChange={setSecondaryColor}
								testId="white-label-secondary-color"
							/>
						</div>

						<div className="presets">
							<div className="presets__lbl">
								{dictionary.white_label_quick_palettes}
							</div>
							<div className="preset-row">
								{presets.map((preset) => (
									<button
										type="button"
										key={preset.name}
										className={
											isPresetActive(preset)
												? 'preset is-active'
												: 'preset'
										}
										onClick={() => onPresetClicked(preset)}
									>
										<span className="preset__dots">
											<span
												className="preset__dot"
												style={{ background: preset.primary }}
											/>
											<span
												className="preset__dot"
												style={{ background: preset.secondary }}
											/>
										</span>
										{preset.name}
									</button>
								))}
							</div>
						</div>

						<div className="wl-logo">
							<div className="wl-logo__thumb">
								{logoPath ? (
									<img src={logoPath} alt="" />
								) : (
									<IconImage />
								)}
							</div>
							<div className="wl-logo__txt">
								<div className="wl-logo__title">
									{dictionary.white_label_logo_title}
								</div>
								<div className="wl-logo__sub">
									{dictionary.white_label_logo_note}
								</div>
							</div>
							<button
								type="button"
								className="wl-logo__go"
								onClick={goToCompanyProfile}
							>
								{dictionary.white_label_logo_cta}
								<IconArrowRight />
							</button>
						</div>
					</div>

					<div className="wl-save">
						<div className="wl-save__note">
							<IconShield />
							{isEnterprise
								? dictionary.white_label_save_note
								: dictionary.white_label_upsell_note}
						</div>
						<div className="wl-save__actions">
							{isEnterprise ? (
								<>
									<button
										type="button"
										className="wl-btn wl-btn--tertiary"
										onClick={onResetClicked}
									>
										{dictionary.reset}
									</button>
									<button
										type="submit"
										className="wl-btn wl-btn--primary"
										disabled={
											isSaving ||
											!primaryValid ||
											!secondaryValid
										}
										data-testid="white-label-save"
									>
										{dictionary.save_changes}
									</button>
								</>
							) : (
								<>
									<button
										type="button"
										className="wl-btn wl-btn--tertiary"
										onClick={goToPlan}
									>
										{dictionary.white_label_see_plan}
									</button>
									<button
										type="button"
										className="wl-btn wl-btn--enterprise"
										onClick={onRequestEnterpriseClicked}
										disabled={isRequesting || !isVerified}
										title={
											!isVerified
												? dictionary.plan_tab_verify_to_upgrade
												: undefined
										}
										data-testid="white-label-request-enterprise"
									>
										{isRequesting
											? dictionary.requesting_enterprise_plan
											: dictionary.plan_tab_upgrade_to_enterprise}
										<IconArrowRight />
									</button>
								</>
							)}
						</div>
					</div>
				</form>

				{/* ---- Right: live preview ---- */}
				<div className="wl__previewcol">
					<div className="pv-top">
						<div className="pv-top__lbl">
							{dictionary.preview}
							<span className="live">
								{dictionary.white_label_preview_live}
							</span>
						</div>
						<div className="pv-seg">
							<button
								type="button"
								className={device === 'desktop' ? 'is-active' : ''}
								onClick={() => setDevice('desktop')}
							>
								<IconMonitor />
								{dictionary.white_label_preview_desktop}
							</button>
							<button
								type="button"
								className={device === 'mobile' ? 'is-active' : ''}
								onClick={() => setDevice('mobile')}
							>
								<IconPhone />
								{dictionary.white_label_preview_mobile}
							</button>
						</div>
					</div>

					<div className="pv-stagewrap">
						<div
							className={
								device === 'mobile'
									? 'pv-stage pv-stage--mobile'
									: 'pv-stage'
							}
							style={{
								'--wl-primary': effectivePrimary,
								'--wl-secondary': effectiveSecondary
							}}
						>
							<div className="bw">
								<div className="bw__bar">
									<div className="bw__dots">
										<i />
										<i />
										<i />
									</div>
									<div className="bw__url">
										<IconLock />
										{previewUrl}
									</div>
								</div>

								<div className="site">
									<div className="site__nav">
										<div className="site__logo">
											<span className="mark">
												{logoPath ? (
													<img src={logoPath} alt="" />
												) : (
													<IconLeaf />
												)}
											</span>
											<span className="name">{brandName}</span>
										</div>
										<div className="site__navlinks">
											<span>
												{dictionary.white_label_preview_nav_tours}
											</span>
											<span>
												{dictionary.white_label_preview_nav_about}
											</span>
											<span className="site__navbtn">
												{dictionary.white_label_preview_nav_book}
											</span>
										</div>
									</div>

									<div
										className="site__hero"
										style={{ backgroundImage: `url(${heroImage})` }}
									>
										<div className="site__herotxt">
											<span className="site__chip">
												{dictionary.white_label_preview_chip}
											</span>
											<h3 className="site__herotitle">
												{dictionary.white_label_preview_exp_title}
											</h3>
										</div>
									</div>

									<div className="site__body">
										<div className="site__meta">
											<span className="site__rating">
												<IconStar />
												4.9 <small>(218)</small>
											</span>
											<span className="site__dot" />
											<span className="site__dur">
												<IconClock />
												{dictionary.white_label_preview_duration}
											</span>
										</div>

										<p className="site__seclbl">
											{dictionary.white_label_preview_choose_date}
										</p>
										<div className="site__dates">
											{previewDates.map((date, idx) => (
												<div
													key={`${date.day}-${date.num}`}
													className={
														idx === 1
															? 'date-pill is-sel'
															: 'date-pill'
													}
												>
													<div className="date-pill__d">
														{date.day}
													</div>
													<div className="date-pill__n">
														{date.num}
													</div>
												</div>
											))}
										</div>

										<div className="site__foot">
											<div className="site__price">
												€89{' '}
												<small>
													{
														dictionary.white_label_preview_per_person
													}
												</small>
											</div>
											<button type="button" className="site__book">
												{dictionary.white_label_preview_book_now}
												<IconArrowRight />
											</button>
										</div>
									</div>

									<div className="site__powered">
										{dictionary.white_label_preview_powered}
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="pv-caption">
						<IconInfo />
						{dictionary.white_label_preview_caption}
					</div>
				</div>
			</div>

			<ToastContainer />
		</div>
	);
};

WhiteLabel.propTypes = {
	info: PropTypes.object,
	goToPlan: PropTypes.func,
	goToCompanyProfile: PropTypes.func
};

export default WhiteLabel;
